/**
 * filterPhonesByType.ts
 *
 * Utility to identify the MAIN phone number from a customer's phone list.
 * The only PhoneTypeId in the API is MAIN. Phones assigned this type have a
 * non-null `type` field; all others have `type: null`.
 */

import { PhoneNumber } from '../integrations/allkort/graphql/interface/Customer';

/**
 * Phone type constants
 * Only one type exists in the API — MAIN.
 * Identified at runtime by a non-null `type` field on the PhoneNumber object.
 */
export const PHONE_TYPES = {
	MAIN: 'MAIN'
} as const;

/**
 * Numeric type IDs as returned by the Allkort API on the PhoneNumber.type object.
 * MAIN corresponds to { id: 1, name: "Huvudtelefonnummer" }.
 */
export const PHONE_TYPE_IDS = {
	MAIN: 1
} as const;

/**
 * Returns the MAIN phone number for a customer, or undefined if none exists.
 * MAIN phones are identified by type.id === PHONE_TYPE_IDS.MAIN (1).
 *
 * @param phones - Array of PhoneNumber objects from currentCustomer.phoneNumbers
 * @returns The MAIN PhoneNumber, or undefined
 *
 * @example
 * const mainPhone = getMainPhoneNumber(customerContactData.currentCustomer.phoneNumbers);
 * if (mainPhone?.verified) {
 *   console.log('Verified MAIN phone:', mainPhone.number);
 * }
 */
export const getMainPhoneNumber = (phones: PhoneNumber[] | undefined): PhoneNumber | undefined => {
	if (!phones || phones.length === 0) return undefined;
	return phones.find(p => p.type?.id === PHONE_TYPE_IDS.MAIN);
};